import {
  bindComparisonReport,
  bindLookalikeReport,
  createReport,
  finalizePaypalSubscription,
  getPayrailsConfig,
  pixelTrackStandard,
  saveUserData,
  sendPdfData,
  trackBingEvent,
  trackEvent,
  updateReport,
} from '@api/requests';
import { PhotoLabEvents, PixelEvents } from '@constants/events';
import { STORAGE_KEYS } from '@hooks/useLocalStorage';
import { EvTruck } from '@models/events';
import { isSubscribeErrorResponse } from '@models/recurly';
import { Box, CircularProgress } from '@mui/material';
import { Payrails, PayrailsEnvironment } from '@payrails/web-sdk';
import { usePaymentConfig } from '@providers/PaymentConfigProvider';
import { wmq } from '@services/magnus';
import { getAttribution } from '@utils/attribution';
import { delay, serializeError } from '@utils/common';
import { formatDateValueToSave } from '@utils/date';
import { getParamsWithDecreasedValue } from '@utils/facebook';
import { pushToDataLayer } from '@utils/gtm';
import { getCookie, setCookie } from '@utils/setCookie';
import { generateHmac } from '@utils/signature';
import {
  getParamsFromSessionStorage,
  setParamsToSessionStorage,
} from '@utils/storage';
import { getPathWithSearchParams } from '@utils/ulr';
import { getIdToken } from 'firebase/auth';
import debounce from 'lodash.debounce';
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Routes } from '../../App';
import { IPaymentPlan } from '../../AppConfig';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import SonaraSubscriptions from '../../lib/sonara-subscriptions';
import { RootState } from '../../store';
import { useQuizState } from '../QuizProvider/QuizProvider.hooks';
import { PaymentMethodType } from '../QuizProvider/QuizProvider.types';
import { useUser } from '../UserProvider';

import { useConfig } from '@providers/ConfigRedirectProvider';
import { ITokenizeData, PaymentContextType } from './PaymentProvider.types';

export const PaymentContext = createContext<PaymentContextType | null>(null);

export const PaymentProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { setQuizData, quizData, userLocation } = useQuizState();
  const { selectedPlan, isMultiCurrency, canPayWithPaypal } =
    usePaymentConfig();
  const [isReadyAppleButton, setReadyAppleButton] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [paymentFormError, setPaymentFormError] = useState<string | null>(null);
  const [applePayError, setApplePayError] = useState<string | null>(null);
  const [error, setError] = useState<null | string>(null);
  const [isPaymentLoading, setPaymentLoading] = useState(false);
  const [signInLoading, setSignInLoading] = useState(false);
  const navigate = useNavigate();
  const { user, firebaseUserCredential } = useUser();
  const [isAdditionalPaymentLoading, setAdditionalPaymentLoading] =
    useState(false);
  const [additionalPaymentError, setAdditionalPaymentError] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    PaymentMethodType.CARD,
  );
  const isTestEnvironment =
    process.env.NODE_ENV === 'development' ||
    window.location.origin.includes('dev');
  const sonaraEnv = isTestEnvironment ? 'staging' : 'production';
  const location = useLocation();
  const selfie = useSelector((state: RootState) => state.onboarding.selfie);

  const subscriptions = new SonaraSubscriptions({
    environment: sonaraEnv,
  });

  const url = subscriptions.getInitSessionURL();
  const payrailsClientRef = useRef<Payrails>(null);
  const comparisonReportId = useSelector(
    (state: RootState) => state.onboarding.comparisonReportId,
  );
  const lookalikeReportId = useSelector(
    (state: RootState) => state.onboarding.lookalikeReportId,
  );

  const [isPaymentContextReady, setPaymentContextReady] = useState(false);

  const [isWaitingForRedirectResponse, setWaitingForRedirectResponse] =
    useState(
      window.localStorage.getItem(STORAGE_KEYS.waitingForRedirectResponse) ===
      'true',
    );

  const config = useConfig();
  const configParams = config.params;

  const hideFbPurchaseEvents = configParams?.hide_fb_purchase_events === 'true';

  useEffect(() => {
    if (isWaitingForRedirectResponse) {
      setTimeout(() => {
        setWaitingForRedirectResponse(false);
        window.localStorage.removeItem(STORAGE_KEYS.waitingForRedirectResponse);
      }, 8000);
    }
  }, [isWaitingForRedirectResponse]);

  useEffect(() => {
    if (
      (location.pathname === Routes.SUBSCRIPTION ||
        location.pathname === Routes.PHOTOLAB_QUIZ ||
        location.pathname === Routes.ETHNICITY_QUIZ) &&
      !payrailsClientRef.current
    ) {
      getPayrailsConfig({ url })
        .then(res => {
          try {
            const config = res.data;
            const options = {
              environment: isTestEnvironment
                ? PayrailsEnvironment.TEST
                : PayrailsEnvironment.PRODUCTION,
              events: {
                onClientInitialized: (
                  execution: any,
                  //WorkflowExecution
                ) => {
                  console.log(
                    'Payrails client initialized',
                    //  execution
                  );
                },
              },
            };

            const payrailsClient = Payrails.init(config, options);

            payrailsClientRef.current = payrailsClient;
            setPaymentContextReady(true);
          } catch (error) {
            console.log('Payrails.init error', error);
          }
        })
        .catch(error => {
          console.log('getPayrailsConfig', error);
          setPaymentContextReady(true);
        });
    } else {
      setPaymentContextReady(true);
    }
  }, [location, isTestEnvironment, url]);

  useEffect(() => {
    if (window.ApplePaySession) {
      setReadyAppleButton(true);
    }
  }, [setQuizData]);

  function handleSubmitForm(tokenizeData: ITokenizeData) {
    setPaymentLoading(true);
    setSelectedPaymentMethod(PaymentMethodType.CARD);

    wmq.track([EvTruck.CardSubmit, EvTruck.ModalPaymentSubmit]);

    subscriptions
      .createSubscriptionNoUserPayrails({
        email: quizData.email,
        plan_id: selectedPlan.id,
        instrument: tokenizeData,
        attribution: getAttribution({
          ip: userLocation?.ip ?? '',
          country: userLocation?.country ?? '',
          config
        }),
        address: {
          first_name: quizData.firstName,
          last_name: quizData.lastName,
        },
        currency: isMultiCurrency ? selectedPlan.currency : 'USD',
      })
      .then(async response => {
        if (isSubscribeErrorResponse(response)) {
          console.error(response.error);
          setPaymentFormError(response.error);
          setPaymentLoading(false);
        } else {
          const eventData = {
            email: quizData.email,
            currency: 'USD',
            value: selectedPlan.trial_sum ?? selectedPlan.sum ?? '',
            transactionId: response.id,
            items: [
              {
                item_id: selectedPlan.id,
                item_name: selectedPlan.name,
              },
            ],
          };

          if (!hideFbPurchaseEvents) {
            pixelTrackStandard(PixelEvents.Purchase, eventData);

            pixelTrackStandard(
              PixelEvents.AllPurchase,
              getParamsWithDecreasedValue(eventData),
            );
          }

          trackBingEvent(PixelEvents.Purchase, { ...eventData, value: 1 });

          trackEvent(
            PixelEvents.AllPurchase,
            config,
            userLocation,
            getParamsWithDecreasedValue(eventData),
          );

          const type = getCookie('onbordingType');
          const photoLabOnboardingOption = getCookie(
            'photoLabOnboardingOption',
          );

          if (type === 'photolab') {
            if (photoLabOnboardingOption === 'skip') {
              trackEvent(
                PhotoLabEvents.PhotolabSkipPurchase,
                config,
                userLocation,
              ).catch(console.error);
            } else if (photoLabOnboardingOption === 'restore') {
              trackEvent(
                PhotoLabEvents.PhotolabRestorePurchase,
                config,
                userLocation,
              ).catch(console.error);
            }
          } else {
            trackEvent(
              !type ? 'MyTraits Onboarding Purchase' : PixelEvents.Purchase,
              config,
              userLocation,
            ).catch(console.error);

            pushToDataLayer(
              !type ? 'MyTraits Onboarding Purchase' : PixelEvents.Purchase,
              {
                userLocation,
                page: window.location.pathname,
              },
            );
          }

          wmq.track(EvTruck.Purchase, {
            email: quizData.email,
            transactionId: response.id,
            productId: selectedPlan.id ?? '',
            revenue: selectedPlan.trial_sum ?? selectedPlan.sum ?? '',
            currency: 'USD',
          });

          setParamsToSessionStorage({
            userEmail: quizData.email,
            subscriptionId: response.id,
            userName: quizData.name !== '' ? quizData.name : '',
            userBirthDate:
              quizData.birth_date !== null ? quizData.birth_date : '',
          });

          if (type !== 'photolab') {
            await createReport({
              email: quizData.email,
              subscription_id: response.id,
              imageBase64: selfie || '',
            });
          }

          if (wmq.isInitialized) {
            await delay(1500);
          }

          navigate(getPathWithSearchParams(Routes.OFFERS));
        }
      })
      .catch(error => {
        const payload: Record<string, any> = {
          email: quizData.email,
          productId: selectedPlan.id ?? '',
          revenue: selectedPlan.trial_sum ?? selectedPlan.sum ?? '',
          currency: 'USD',
        };

        if (typeof error.error === 'string') {
          setPaymentFormError(error.error);
          payload.error = error.error;
        }

        wmq.track(EvTruck.PurchaseError, payload);
      })
      .finally(() => {
        setPaymentLoading(false);
      });
  }

  const handlePaypalSubscription = async (subscriptionId: string) => {
    if (!subscriptionId) return;

    setPaymentLoading(true);
    setSelectedPaymentMethod(PaymentMethodType.PAYPAL);

    try {
      const type = getCookie('onbordingType');

      setParamsToSessionStorage({
        userEmail: quizData.email,
        subscriptionId,
        userName: quizData.name !== '' ? quizData.name : '',
        userBirthDate: quizData.birth_date ?? '',
      });

      if (type !== 'photolab') {
        await createReport({
          email: quizData.email,
          subscription_id: subscriptionId,
          imageBase64: selfie || '',
        });
      }

      if (wmq.isInitialized) {
        await delay(1500);
      }

      navigate(getPathWithSearchParams(Routes.OFFERS));
    } catch (error: any) {
      console.error('handlePaypalSubscription error', error);
    } finally {
      setPaymentLoading(false);
    }
  };

  // Finalize Subscriptions
  useUpdateEffect(() => {
    const { userName, userBirthDate, subscriptionId } =
      getParamsFromSessionStorage([
        'userName',
        'userBirthDate',
        'subscriptionId',
      ]);

    if (user && subscriptionId) {
      setSignInLoading(true);
      setError(null);

      const appleToken = user.isAppleAuth
        ? (user.providerDataId ?? user.uid)
        : user.uid;

      const finalize = async (subId: string, plan?: string) => {
        return subscriptions.finalizeSubscription({
          subscription_id: subId,
          email: user.email,
          firebase_id: !user.isAppleAuth ? appleToken : undefined,
          apple_token: user.isAppleAuth ? appleToken : undefined,
          attribution: getAttribution({
            ip: userLocation?.ip ?? '',
            country: userLocation?.country ?? '',
            config
          }),
          plan,
        });
      };

      const run = async () => {
        const wasWithPaypalPayment =
          getCookie('wasWithPaypalPayment') === 'true';

        try {
          if (wasWithPaypalPayment) {
            const payPalOffersOrdersIdsCookieValue = getCookie(
              'payPalOffersOrdersIds',
            );
            const payPalOffersOrdersIds: Array<{
              orderId: string;
              plan: string;
            }> = payPalOffersOrdersIdsCookieValue
                ? JSON.parse(payPalOffersOrdersIdsCookieValue)
                : [];

            for (const item of payPalOffersOrdersIds) {
              await finalize(item.orderId, item.plan);
            }

            const payPalSubscriptionCookie = getCookie('payPalSubscription');

            if (payPalSubscriptionCookie) {
              try {
                const payPalSubscription = JSON.parse(payPalSubscriptionCookie);

                const paypalFinalizeResponse = await finalizePaypalSubscription(
                  {
                    subscription_id: payPalSubscription.subscriptionId || '',
                    email: user.email,
                    firebase_id: !user.isAppleAuth ? appleToken : undefined,
                    apple_token: user.isAppleAuth ? appleToken : undefined,
                    attribution: getAttribution({
                      ip: userLocation?.ip ?? '',
                      country: userLocation?.country ?? '',
                      config
                    }),
                    plan: payPalSubscription.plan || '',
                  },
                );

                setCookie('wasWithPaypalPayment', 'true');

                console.log(
                  'PayPal finalize subscription response:',
                  paypalFinalizeResponse.data,
                );

                const type = getCookie('onbordingType');

                if (type !== 'photolab') {
                  await updateReport({
                    subscription_id: payPalSubscription.subscriptionId || '',
                    user_id: user.uid,
                  });
                }
              } catch (err) {
                console.error('Failed to finalize PayPal subscription:', err);
              }
            }
          } else {
            let response;

            try {
              response = await finalize(subscriptionId);
            } catch (err) {
              console.error('finalize failed', err);
            }

            console.log('response finalise', response);
            // if (response?.id) {
            // console.log('Finalize response id:', response.id);

            const type = getCookie('onbordingType');

            if (type !== 'photolab') {
              await updateReport({
                subscription_id: subscriptionId,
                user_id: user.uid,
              });
            }
            // }
          }

          if (firebaseUserCredential && (userName || userBirthDate)) {
            const idToken = await getIdToken(firebaseUserCredential, true);
            await saveUserData({
              ...(userName ? { name: userName } : {}),
              ...(userBirthDate
                ? { birth_date: formatDateValueToSave(userBirthDate) }
                : {}),
              idToken,
            }).catch(err => console.error('saveUserData err', err));

            if (comparisonReportId) {
              const bindData = { id: comparisonReportId, user_id: user.uid };
              const xRequestSig = generateHmac(JSON.stringify(bindData));
              await bindComparisonReport({ xRequestSig, ...bindData }).catch(
                console.error,
              );
            }

            if (lookalikeReportId) {
              const bindData = { id: lookalikeReportId, user_id: user.uid };
              const xRequestSig = generateHmac(JSON.stringify(bindData));
              await bindLookalikeReport({ xRequestSig, ...bindData }).catch(
                console.error,
              );
            }
          }

          window.sessionStorage.removeItem('userEmail');
          window.sessionStorage.removeItem('subscriptionId');

          setSignInLoading(false);
          navigate(getPathWithSearchParams(Routes.WEBAPP_MAIN));
        } catch (err: any) {
          console.error('finalizeSubscriptionSonara err', err);
          setError(err.error ?? 'Finalize error');
          setSignInLoading(false);
        }
      };

      run();
    }
  }, [user, lookalikeReportId, comparisonReportId, canPayWithPaypal]);

  // Ref для отслеживания активной сессии Apple Pay
  const activeApplePaySession = useRef<ApplePaySession | null>(null);

  const handleSonaraPayByApple = async () => {
    // Проверка что нет активной сессии
    if (activeApplePaySession.current) {
      console.warn(
        '[Apple Pay] Session already active, ignoring duplicate call',
      );
      return;
    }

    // Проверка что не идет процесс оплаты
    if (isPaymentLoading) {
      console.warn(
        '[Apple Pay] Payment already in progress, ignoring duplicate call',
      );
      return;
    }

    console.log('[Apple Pay] Starting new session...');

    // Track Apple Pay initialization start
    trackEvent('apple_pay_init_start', config, userLocation, {
      email: quizData.email,
      timestamp: Date.now(),
    });

    setPaymentLoading(true);
    setApplePayError(null); // Очистить предыдущие ошибки
    setSelectedPaymentMethod(PaymentMethodType.APPLE);
    wmq.track([EvTruck.ApplePaySubmit, EvTruck.ModalPaymentSubmit]);
    let session: ApplePaySession;
    try {
      session = await subscriptions.initApplePaySession({
        currency: selectedPlan.currency || 'USD',
        amount: Number(selectedPlan.trial_sum ?? selectedPlan.sum),
        label: selectedPlan.name_full ?? '',
        paymentDescription: selectedPlan.name_full ?? '',
        managementURL: window.location.origin + Routes.SIGN_IN,
      });

      // Сохраняем ссылку на активную сессию СРАЗУ после создания
      activeApplePaySession.current = session;

      console.log('[Apple Pay] Session initialized successfully');
      console.log('Initialized session', session);
      trackEvent('apple_pay_session_initialized', config, userLocation, {
        email: quizData?.email,
        session: {
          label: selectedPlan.name_full ?? '',
          managementURL: window.location.origin + Routes.SIGN_IN,
        },
        timestamp: Date.now(),
      });
    } catch (err) {
      console.error('[Apple Pay] Failed to initialize session:', err);
      setApplePayError('Failed to initialize Apple Pay session');
      setPaymentLoading(false);
      activeApplePaySession.current = null;

      trackEvent('apple_pay_init_error', config, userLocation, {
        error: serializeError(err),
        email: quizData?.email,
        session: {
          label: selectedPlan.name_full ?? '',
          managementURL: window.location.origin + Routes.SIGN_IN,
        },
      });

      return;
    }

    session.oncancel = function (event) {
      console.log('session.oncancel', event);
      setApplePayError('Payment was cancelled');
      activeApplePaySession.current = null;

      trackEvent('apple_pay_user_cancelled', config, userLocation, {
        email: quizData?.email,
        session: {
          label: selectedPlan.name_full ?? '',
          managementURL: window.location.origin + Routes.SIGN_IN,
        },
      });

      setPaymentLoading(false);
    };

    session.onpaymentauthorized = function (event) {
      // console.log('onpaymentauthorized', event);
      trackEvent('apple_pay_payment_authorized', config, userLocation, {
        email: quizData?.email,
        session: {
          label: selectedPlan.name_full ?? '',
          managementURL: window.location.origin + Routes.SIGN_IN,
        },
        displayName: event?.payment?.token?.paymentMethod?.displayName,
        billingContact: event?.payment?.billingContact,
      });
      const token = event.payment.token.paymentData;
      const displayName = event.payment.token.paymentMethod?.displayName;

      const address = event.payment.billingContact?.addressLines
        ? event.payment.billingContact.addressLines[0]
        : null;
      const address2 = event.payment.billingContact?.addressLines
        ? event.payment.billingContact.addressLines[1]
        : null;

      const vaultParameter = new URLSearchParams(window.location.search).get(
        'vault',
      );
      const vault = vaultParameter === '' ? null : vaultParameter;

      subscriptions
        .createSubscriptionNoUserApplePay({
          email: quizData.email,
          plan_id: selectedPlan.id ?? '',
          attribution: getAttribution({
            ip: userLocation?.ip ?? '',
            country: userLocation?.country ?? '',
            config
          }),
          currency: isMultiCurrency ? selectedPlan.currency : 'USD',
          token,
          display_name: displayName,
          address: {
            first_name: event.payment.billingContact?.givenName,
            last_name: event.payment.billingContact?.familyName,
            country: event.payment.billingContact?.countryCode,
            state: event.payment.billingContact?.administrativeArea,
            zip: event.payment.billingContact?.postalCode,
            city: event.payment.billingContact?.locality,
            ...(address && { address }),
            ...(address2 && { address2 }),
            phone: event.payment.billingContact?.phoneNumber,
          },
          vault,
        })
        .then(async response => {
          if (response.id) {
            const eventData = {
              email: quizData.email,
              currency: 'USD',
              value: selectedPlan.trial_sum ?? selectedPlan.sum ?? '',
              transactionId: response.id,
              items: [
                {
                  item_id: selectedPlan.id,
                  item_name: selectedPlan.name,
                },
              ],
            };

            if (!hideFbPurchaseEvents) {
              pixelTrackStandard(PixelEvents.Purchase, eventData);

              pixelTrackStandard(
                PixelEvents.AllPurchase,
                getParamsWithDecreasedValue(eventData),
              );
            }

            trackBingEvent(PixelEvents.Purchase, { ...eventData, value: 1 });

            trackEvent(
              PixelEvents.AllPurchase,
              config,
              userLocation,
              getParamsWithDecreasedValue(eventData),
            );

            const type = getCookie('onbordingType');
            const photoLabOnboardingOption = getCookie(
              'photoLabOnboardingOption',
            );

            if (type === 'photolab') {
              if (photoLabOnboardingOption === 'skip') {
                trackEvent(
                  PhotoLabEvents.PhotolabSkipPurchase,
                  config,
                  userLocation,
                ).catch(console.error);
              } else if (photoLabOnboardingOption === 'restore') {
                trackEvent(
                  PhotoLabEvents.PhotolabRestorePurchase,
                  config,
                  userLocation,
                ).catch(console.error);
              }
            } else {
              trackEvent(
                !type ? 'MyTraits Onboarding Purchase' : PixelEvents.Purchase,
                config,
                userLocation,
              ).catch(console.error);

              pushToDataLayer(
                !type ? 'MyTraits Onboarding Purchase' : PixelEvents.Purchase,
                {
                  userLocation,
                  page: window.location.pathname,
                },
              );
            }

            wmq.track(EvTruck.Purchase, {
              email: quizData.email,
              transactionId: response.id,
              productId: selectedPlan.id ?? '',
              paymentSystem: 'sonara',
              revenue: selectedPlan.trial_sum ?? selectedPlan.sum ?? '',
              currency: 'USD',
            });

            session.completePayment(ApplePaySession.STATUS_SUCCESS);
            activeApplePaySession.current = null;

            setParamsToSessionStorage({
              userEmail: quizData.email,
              subscriptionId: response.id,
              userName: quizData.name !== '' ? quizData.name : '',
              userBirthDate:
                quizData.birth_date !== null ? quizData.birth_date : '',
            });

            if (type !== 'photolab') {
              await createReport({
                email: quizData.email,
                subscription_id: response.id,
                imageBase64: selfie || '',
              });
            }

            navigate(getPathWithSearchParams(Routes.OFFERS));

            trackEvent(
              'apple_pay_subscription_creation_success',
              config,
              userLocation,
              {
                email: quizData?.email,
                session: {
                  label: selectedPlan.name_full ?? '',
                  managementURL: window.location.origin + Routes.SIGN_IN,
                },
                subscriptionId: response.id,
              },
            );
          } else {
            const errMessage =
              response.error ??
              response.message ??
              'Something went wrong, cant create subscription';
            setApplePayError(errMessage);
            activeApplePaySession.current = null;
            trackEvent('apple_pay_subscription_creation_failed', config, userLocation, {
              response: JSON.stringify(response),
              error: serializeError(
                errMessage || 'No subscription ID in response',
              ),
              email: quizData?.email,
            });
          }
        })
        .catch(err => {
          const errorMessage =
            err.error ??
            err.message ??
            'Something went wrong, cant create subscription';

          const payload: Record<string, any> = {
            email: quizData.email,
            productId: selectedPlan.id ?? '',
            paymentSystem: 'sonara',
            revenue: selectedPlan.trial_sum ?? selectedPlan.sum ?? '',
            currency: 'USD',
            error: errorMessage,
          };

          setApplePayError(errorMessage);
          activeApplePaySession.current = null;

          trackEvent('apple_pay_subscription_creation_failed', config, userLocation, {
            error: serializeError(errorMessage),
            errorDetails: serializeError(err),
            email: quizData?.email,
            productId: selectedPlan?.id ?? '',
          });

          wmq.track(EvTruck.PurchaseError, payload);

          session.completePayment(ApplePaySession.STATUS_FAILURE);
        })
        .finally(() => {
          setPaymentLoading(false);
        });
    };

    trackEvent('apple_pay_session_start', config, userLocation, {
      email: quizData?.email,
      session: {
        label: selectedPlan.name_full ?? '',
        managementURL: window.location.origin + Routes.SIGN_IN,
      },
    });

    session.begin();

    trackEvent('apple_pay_modal_opened', config, userLocation, {
      email: quizData?.email,
      userId: user?.uid,
      session: {
        label: selectedPlan.name_full ?? '',
        managementURL: window.location.origin + Routes.SIGN_IN,
      },
      timestamp: Date.now(),
    });
  };

  const handleAdditionalOfferAdd = useCallback(
    (
      additionalPlan: IPaymentPlan,
      onSuccess: () => void,
      isSkipTrialPlan?: boolean,
      isOffersFlow?: boolean,
    ) => {
      if (isAdditionalPaymentLoading) return;

      trackEvent('apple_pay_additional_offer_called', config, userLocation, {
        planId: additionalPlan?.id,
        timestamp: Date.now(),
      }).catch(console.error);

      const { userEmail, subscriptionId } = getParamsFromSessionStorage([
        'userEmail',
        'subscriptionId',
      ]);

      if (userEmail && subscriptionId) {
        setAdditionalPaymentError('');
        setAdditionalPaymentLoading(true);

        trackEvent('apple_pay_additional_offer_start', config, userLocation, {
          email: userEmail,
          planId: additionalPlan.id,
          timestamp: Date.now(),
        });

        subscriptions
          .createSubscriptionNoUserWithSubId({
            email: userEmail,
            plan_id: additionalPlan.id,
            subscription_id: subscriptionId,
            attribution: getAttribution({
              ip: userLocation?.ip ?? '',
              country: userLocation?.country ?? '',
              config
            }),
            currency: isMultiCurrency ? additionalPlan.currency : 'USD',
          })
          .then(async response => {
            if (response.id) {
              const eventData = {
                email: userEmail,
                currency: 'USD',
                value: additionalPlan.trial_sum ?? additionalPlan.sum,
                transactionId: response.id,
                items: [
                  {
                    item_id: additionalPlan.id,
                    item_name: additionalPlan.name,
                  },
                ],
              };

              trackEvent(
                'apple_pay_subscription_creation_success',
                config,
                userLocation,
                {
                  email: userEmail,
                  subscriptionId: response.id,
                },
              );

              if (!hideFbPurchaseEvents) {
                pixelTrackStandard(
                  PixelEvents.AllPurchase,
                  getParamsWithDecreasedValue(eventData),
                );
              }

              trackEvent(
                PixelEvents.AllPurchase,
                config,
                userLocation,
                getParamsWithDecreasedValue(eventData),
              );

              if (!isSkipTrialPlan) {
                pixelTrackStandard(
                  PixelEvents.OneTimePurchase,
                  getParamsWithDecreasedValue(eventData),
                );
                trackEvent(
                  PixelEvents.OneTimePurchase,
                  config,
                  userLocation,
                  getParamsWithDecreasedValue(eventData),
                );
              }

              trackEvent(`Purchase ${additionalPlan.name}`, config, userLocation).catch(
                console.error,
              );

              pushToDataLayer(`Purchase ${additionalPlan.name}`, {
                userLocation,
                page: window.location.pathname,
              });

              wmq.track(EvTruck.AdditionalPurchase, {
                email: userEmail,
                transactionId: response.id,
                productId: additionalPlan?.id ?? '',
                paymentSystem: 'sonara',
                revenue: additionalPlan?.trial_sum ?? additionalPlan?.sum ?? '',
                currency: 'USD',
              });

              const type = getCookie('onbordingType');
              console.log('type123123', type);

              if (isOffersFlow && !isSkipTrialPlan && type === 'photolab') {
                const cookieName = 'offersSubscriptionIds';

                const existing = getCookie(cookieName);
                const ids: string[] = existing ? JSON.parse(existing) : [];

                if (!ids.includes(response.id)) {
                  ids.push(response.id);
                }

                setCookie(cookieName, JSON.stringify(ids));
              }

              if (!isSkipTrialPlan && type !== 'photolab') {
                await sendPdfData({
                  email: userEmail,
                  subscription_id: response.id,
                  imageBase64: selfie || '',
                  name: quizData.name,
                  birth_date: quizData.birth_date?.toString(),
                });
              }

              onSuccess();
            }
            if (response.error) {
              setAdditionalPaymentError(response.error);
            }
          })
          .catch(err => {
            const errorMessage = err.error ?? 'Something went wrong';
            const payload: Record<string, any> = {
              email: userEmail,
              productId: additionalPlan.id,
              paymentSystem: 'sonara',
              revenue: additionalPlan?.trial_sum ?? additionalPlan?.sum ?? '',
              currency: 'USD',
              error: errorMessage,
            };

            wmq.track(EvTruck.AdditionalPurchaseError, payload);

            trackEvent(
              `Purchase error ${additionalPlan.name}`,
              config,
              userLocation,
            ).catch(console.error);

            setAdditionalPaymentError(errorMessage);
          })
          .finally(() => {
            setAdditionalPaymentLoading(false);
          });
      } else {
        setAdditionalPaymentError('No subscription data');
      }
    },
    [isAdditionalPaymentLoading, selfie],
  );

  const debouncedHandleAdditionalOfferAdd = useCallback(
    debounce(handleAdditionalOfferAdd, 1000, {
      leading: true,
      trailing: false,
    }),
    [handleAdditionalOfferAdd],
  );

  const onSendPdfDataReport = async (
    subscriptionId: string,
    selfie: string | null,
  ) => {
    console.log('onSendPdfDataReport selfie', selfie);
    if (!selfie) return;

    const { userEmail } = getParamsFromSessionStorage(['userEmail']);

    await sendPdfData({
      email: userEmail,
      subscription_id: subscriptionId,
      imageBase64: selfie || '',
      name: quizData.name,
      birth_date: quizData.birth_date?.toString(),
    });
  };

  const value: PaymentContextType = {
    isReadyAppleButton,
    handleSonaraPayByApple,
    handleAdditionalOfferAdd: debouncedHandleAdditionalOfferAdd,
    handleSubmitForm,
    paymentFormError,
    setPaymentFormError,
    applePayError,
    setApplePayError,
    formRef,
    isPaymentLoading,
    setPaymentLoading,
    signInLoading,
    setSignInLoading,
    error,
    setError,
    isAdditionalPaymentLoading,
    setAdditionalPaymentLoading,
    additionalPaymentError,
    setAdditionalPaymentError,
    subscriptions,
    payrailsClient: payrailsClientRef.current,
    isWaitingForRedirectResponse,
    onSendPdfDataReport,
    handlePaypalSubscription,
  };

  if (!isPaymentContextReady) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#fff',
          zIndex: 1000,
        }}
      >
        <CircularProgress size={32} style={{ color: '#040B1D' }} />
      </Box>
    );
  }

  return (
    <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
  );
};
